import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { TOOLS, toolPath } from "../lib/tools";

export default function FeaturedTools() {
  return (
    <section id="tools" className="bg-white py-24 lg:py-32 border-t border-slate-200">
      <div className="max-w-wide mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-emerald-strong mb-4">
              Free tools · no signup
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-slate-950 max-w-2xl">
              Run the numbers before you decide.
            </h2>
          </div>
          <div className="lg:col-span-4">
            {/* Iter-220 — Supporting copy names the calculator
                verticals (auto loan, refinance, insurance) so the
                section carries keyword relevance without changing
                the brand-voice H2.
                Iter-275.AX (Phase 5 #22) — count now sourced from
                TOOLS.length so the homepage strip never drifts from
                the canonical registry. */}
            <p className="text-slate-600 leading-relaxed max-w-md lg:ml-auto">
              {TOOLS.length} free auto loan, refinance, and insurance
              calculators to estimate payments, compare savings, and
              understand the true cost of car ownership — no signup
              required.
            </p>
            {/* Iter-275.AX (Phase 5 #22) — Explicit deep-link to the
                /calculators index for visitors who want the full
                taxonomy view (track sub-nav + methodology callout) on
                a single page. Closes the audit-flagged "orphan
                calculator surface" gap: every calculator is reachable
                in ≤ 2 clicks from `/` via this card OR the per-tool
                cards in the grid below. */}
            <Link
              to="/calculators"
              data-testid="featured-tools-see-all"
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-slate-950 hover:gap-3 transition-all lg:ml-auto lg:flex"
            >
              See all {TOOLS.length} calculators
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {TOOLS.map(({ slug, path, title, description, Icon, placeholder }) => (
            <Link
              key={slug}
              to={toolPath({ slug, path })}
              data-testid={`tool-card-${slug}`}
              className="group bg-white border border-slate-200 p-8 flex flex-col justify-between min-h-[220px] transition-all hover:-translate-y-1 hover:border-slate-950"
            >
              <div>
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 bg-[#10B981]/10 grid place-items-center">
                    <Icon className="w-6 h-6 text-[#0A2540]" strokeWidth={1.6} />
                  </div>
                  {placeholder && (
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                      Coming soon
                    </span>
                  )}
                </div>
                <h3 className="font-display font-bold text-2xl text-slate-950 leading-tight mb-3">
                  {title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{description}</p>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 font-bold text-sm text-slate-950">
                Open tool
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
            </Link>
          ))}
          {/* Iter-275.AX (Phase 5 #22) — Anchor card at the tail of
              the strip that links to the full /calculators index. Reads
              as the natural "what's next" beat after scrolling through
              the per-tool cards. Different visual treatment (navy slab)
              so it doesn't compete with the individual calculator
              cards for primary-CTA attention. */}
          <Link
            to="/calculators"
            data-testid="featured-tools-cluster-card"
            className="group bg-[#0A2540] text-white p-8 flex flex-col justify-between min-h-[220px] transition-all hover:-translate-y-1 hover:bg-slate-800 lg:col-span-1"
          >
            <div>
              <div className="flex items-start justify-between mb-8">
                <div className="w-12 h-12 bg-[#10B981] grid place-items-center">
                  <ArrowUpRight className="w-6 h-6 text-[#0A2540]" strokeWidth={1.75} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#10B981]">
                  All tools
                </span>
              </div>
              <h3 className="font-display font-bold text-2xl text-white leading-tight mb-3">
                Browse all {TOOLS.length} calculators
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                Grouped by Loans, Insurance, Buying &amp; Ownership, and
                Warranty — with a quick preview of each result so you
                know what you&apos;ll get before you open the tool.
              </p>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 font-bold text-sm">
              See all calculators
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
